// src/components/HRComponents/CaseTrackerList.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { 
  Briefcase, CheckCircle2, Clock, Eye, AlertCircle, Search, Filter, Plus 
} from "lucide-react";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PageLoader from "../../helpers/PageLoader";
import SelectDropdown from "../../customStyles/SelectDropdown";
import ModernTable from "../../components/ui/ModernTable";
import CaseDetailModal from "../../components/HRComponents/CaseDetailModal";
import CaseCreateDrawer from "../../components/HRComponents/CaseCreateDrawer";

export default function CaseTrackerList({ scope, user }) {
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);

  const [isCaseDrawerOpen, setIsCaseDrawerOpen] = useState(false);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState(""); // empty = All, "assigned" = Assigned, "unassigned" = Unassigned
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const loadCasesData = useCallback(async () => {
    setLoading(true);
    try {
      const casesUrl = SummaryApi.getAllCases?.url || "/api/cases/view-all";
      const categoriesUrl = SummaryApi.getAllCaseCategories?.url || "/api/case-categories/view-all";
      const employeesUrl = SummaryApi.getAllEmployees?.url || "/api/users/view-all";

      // Configure parameters dynamically based on scope
      const params = {};
      if (scope === "raised") params.raisedBy = user._id;
      if (scope === "assigned") params.assignedTo = user._id;
      if (scope === "team") params.reportingManagerId = user._id;

      const [casesRes, catsRes, empsRes] = await Promise.all([
        api.get(casesUrl, { params }),
        api.get(categoriesUrl),
        api.get(employeesUrl).catch(() => ({ data: { success: false, data: [] } }))
      ]);

      if (casesRes.data.success) {
        setCases(casesRes.data.items || casesRes.data.data || []);
      }
      if (catsRes.data.success) {
        setCategories(catsRes.data.data.map(c => ({ label: c.name, value: c._id })));
      }
      if (empsRes.data.success) {
        setEmployees(empsRes.data.data.map(e => ({ label: `${e.firstName} ${e.lastName}`, value: e._id })));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load cases.");
    } finally {
      setLoading(false);
    }
  }, [scope, user]);

  useEffect(() => {
    loadCasesData();
  }, [loadCasesData]);


  // Comprehensive Client-side Filter Implementation
  const filteredCases = useMemo(() => {
    return cases.filter(c => {
      const matchesSearch = c.title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            c.caseId?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // 🔐 Safe Comparison: MongoDB ID-களை பாதுகாப்பாக String-ஆக மாற்றி ஒப்பிடுதல்
      const caseCatId = c.categoryId?._id || c.categoryId;
      const matchesCategory = !selectedCategory || String(caseCatId) === String(selectedCategory);
      
      const matchesStatus = !selectedStatus || c.status === selectedStatus;
      const matchesPriority = !selectedPriority || c.priority === selectedPriority;

      // Handle the Assignment status filter
      let matchesAssignment = true;
      if (assignmentFilter === "assigned") {
        matchesAssignment = !!c.assignedTo;
      } else if (assignmentFilter === "unassigned") {
        matchesAssignment = !c.assignedTo;
      }

      return matchesSearch && matchesCategory && matchesStatus && matchesPriority && matchesAssignment;
    });
  }, [cases, searchQuery, selectedCategory, selectedStatus, selectedPriority, assignmentFilter]);
  const paginatedCases = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredCases.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredCases, currentPage]);

  // Metrics summary calculated dynamically
  const stats = useMemo(() => {
    const total = filteredCases.length;
    const pending = filteredCases.filter(c => c.status === "pending").length;
    const inReview = filteredCases.filter(c => c.status === "in_review").length;
    const resolved = filteredCases.filter(c => ["approved", "rejected", "closed"].includes(c.status)).length;
    return { total, pending, inReview, resolved };
  }, [filteredCases]);

  const columns = useMemo(() => [
    {
      key: "caseId",
      label: "Case ID",
      render: (row) => <span className="font-bold text-slate-700 whitespace-nowrap">{row.caseId}</span>
    },
    {
      key: "title",
      label: "Subject",
      render: (row) => (
        <div className="text-left max-w-[240px]">
          <p className="font-bold text-slate-900 truncate">{row.title}</p>
          <p className="text-[10px] text-slate-400 truncate mt-0.5">{row.categoryId?.name}</p>
        </div>
      )
    },
    {
      key: "raisedBy",
      label: "Raised By",
      render: (row) => {
        const u = row.raisedBy || {};
        return (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0 font-bold text-slate-600 text-[10px]">
              {u.firstName?.[0] || ""}{u.lastName?.[0] || ""}
            </div>
            <p className="font-semibold text-slate-800 text-xs whitespace-nowrap">{u.firstName} {u.lastName}</p>
          </div>
        );
      }
    },
    {
      key: "assignedTo",
      label: "Assignee",
      render: (row) => {
        const u = row.assignedTo || {};
        return u.firstName ? (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-red-50 text-red-600 border border-red-100 flex items-center justify-center shrink-0 font-bold text-[10px]">
              {u.firstName?.[0] || ""}{u.lastName?.[0] || ""}
            </div>
            <p className="font-medium text-slate-700 text-xs whitespace-nowrap">{u.firstName} {u.lastName}</p>
          </div>
        ) : (
          <span className="text-xs text-slate-400 italic">Unassigned</span>
        );
      }
    },
    {
      key: "priority",
      label: "Priority",
      render: (row) => {
        const colors = {
          low: "bg-slate-50 text-slate-600 border-slate-200",
          medium: "bg-blue-50 text-blue-600 border-blue-200",
          high: "bg-orange-50 text-orange-600 border-orange-200",
          urgent: "bg-red-50 text-red-600 border-red-200 animate-pulse"
        };
        return (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wide ${colors[row.priority]}`}>
            {row.priority}
          </span>
        );
      }
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const colors = {
          pending: "bg-slate-50 text-slate-500 border-slate-200",
          in_review: "bg-amber-50 text-amber-600 border-amber-200",
          approved: "bg-green-50 text-green-700 border-green-200",
          rejected: "bg-red-50 text-red-700 border-red-200",
          closed: "bg-emerald-50 text-emerald-800 border-emerald-300",
          reopened: "bg-indigo-50 text-indigo-700 border-indigo-200"
        };
        return (
          <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border uppercase ${colors[row.status]}`}>
            {row.status?.replace("_", " ")}
          </span>
        );
      }
    }
  ], []);

  const tableActions = useMemo(() => [
    {
      label: "View Details",
      icon: <Eye className="w-4 h-4 text-slate-500" />,
      onClick: (row) => setSelectedCase(row)
    }
  ], []);

  const handleClearFilters = () => {
    setSelectedCategory("");
    setSelectedStatus("");
    setSelectedPriority("");
    setAssignmentFilter("");
    setSearchQuery("");
  };

  if (loading) return <PageLoader />;

  return (
    <>
    <div className="space-y-4 text-left">
      
      {/* Header operations */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
            {scope === "raised" ? "My Personal Grievances" : scope === "assigned" ? "Tasks Pending Resolution" : scope === "team" ? "Team Incident Reports" : "Corporate Central Tracker"}
          </h2>
        </div>
        {scope === "raised" && (
          <button 
            onClick={() => setIsCaseDrawerOpen(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold h-9 shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> Raise a Case
          </button>
        )}
      </div>

      {/* Analytics Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border rounded-2xl p-4 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center text-slate-600 shrink-0"><Briefcase className="w-5 h-5" /></div>
          <div className="text-left"><span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total</span><p className="text-xl font-bold text-slate-900 mt-0.5">{stats.total}</p></div>
        </div>
        <div className="bg-white border rounded-2xl p-4 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-50 border border-amber-100 rounded-full flex items-center justify-center text-amber-600 shrink-0"><Clock className="w-5 h-5" /></div>
          <div className="text-left"><span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Pending</span><p className="text-xl font-bold text-slate-900 mt-0.5">{stats.pending}</p></div>
        </div>
        <div className="bg-white border rounded-2xl p-4 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center text-blue-600 shrink-0"><AlertCircle className="w-5 h-5" /></div>
          <div className="text-left"><span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">In Review</span><p className="text-xl font-bold text-slate-900 mt-0.5">{stats.inReview}</p></div>
        </div>
        <div className="bg-white border rounded-2xl p-4 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 bg-green-50 border border-green-100 rounded-full flex items-center justify-center text-green-600 shrink-0"><CheckCircle2 className="w-5 h-5" /></div>
          <div className="text-left"><span className="block text-[10px] text-slate-400 font-bold uppercase tracking-wider">Resolved</span><p className="text-xl font-bold text-slate-900 mt-0.5">{stats.resolved}</p></div>
        </div>
      </div>

      {/* Interactive Filters Panel */}
      <div className="bg-white border rounded-xl p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-44">
            <SelectDropdown options={categories} value={selectedCategory} onChange={setSelectedCategory} placeholder="All Categories" valueKey="value" labelKey="label" ChildClassName="h-9 text-xs border-slate-200" searchable={true} />
          </div>
          <div className="w-44">
            <SelectDropdown options={[{ label: "Pending", value: "pending" }, { label: "In Review", value: "in_review" }, { label: "Approved", value: "approved" }, { label: "Rejected", value: "rejected" }, { label: "Closed", value: "closed" }]} value={selectedStatus} onChange={setSelectedStatus} placeholder="All Status" valueKey="value" labelKey="label" ChildClassName="h-9 text-xs border-slate-200" />
          </div>
          <div className="w-44">
            <SelectDropdown options={[{ label: "Low", value: "low" }, { label: "Medium", value: "medium" }, { label: "High", value: "high" }, { label: "Urgent", value: "urgent" }]} value={selectedPriority} onChange={setSelectedPriority} placeholder="All Priorities" valueKey="value" labelKey="label" ChildClassName="h-9 text-xs border-slate-200" />
          </div>
          {/* Newly added Assignment Status filter */}
          <div className="w-44">
            <SelectDropdown options={[{ label: "Assigned Cases Only", value: "assigned" }, { label: "Unassigned Cases Only", value: "unassigned" }]} value={assignmentFilter} onChange={setAssignmentFilter} placeholder="All Assignments" valueKey="value" labelKey="label" ChildClassName="h-9 text-xs border-slate-200" />
          </div>
          <button onClick={handleClearFilters} className="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-semibold h-9 border-slate-200 hover:bg-slate-50 transition-colors"><Filter className="h-3.5 w-3.5" /> Reset</button>
        </div>
        <div className="relative text-left">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search cases..." className="w-full border border-slate-200 rounded-lg pl-9 pr-4 py-1.5 text-xs h-9 focus:ring-1 focus:ring-red-200 outline-none" />
        </div>
      </div>

      {/* Main Table View */}
      <ModernTable columns={columns} data={paginatedCases} actions={tableActions} actionType="dropdown" selectable={false} pagination={{ totalItems: filteredCases.length, itemsPerPage, currentPage, onPageChange: setCurrentPage }} />

      {/* Dynamic Detail Modal */}
      {selectedCase && (
        <CaseDetailModal 
          selectedCase={selectedCase} 
          setSelectedCase={setSelectedCase} 
          user={user} 
          employees={employees} // Passed employees array down to allow HR Reassignment
          onUpdate={loadCasesData}
        />
      )}
    </div>
    
    {/* Dynamic Raise Drawer */}
    <CaseCreateDrawer 
      isOpen={isCaseDrawerOpen} 
      onClose={() => setIsCaseDrawerOpen(false)} 
      categories={categories} 
      onSuccess={loadCasesData}
    />
    </>
  );
}